import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { PropType } from 'vue';
import { notification } from 'ant-design-vue';
import { debounce } from 'vue-debounce';
import AddOfferItems from '@/components/global/popup/AddOfferItems.vue';
import SegmentedMultiposition from '@/components/views/project/SegmentedMultiposition.vue';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { RouteNames } from '@/enums/routes/RouteNames';
import { EventBus } from '@/helpers/EventBusHelper';
import {
    cssMultipositionRowElementPositioning,
    extractDefaultValues,
    extractUpdateFunctions,
} from '@/helpers/FormDataHelper';
import { checkForErrorsInFieldsAndReturnState, resetHelperState } from '@/helpers/FormValidationHelper';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import type { ChecklistField } from '@/interfaces/components/configurator/ChecklistField';
import type { ChecklistFieldEntry } from '@/interfaces/components/configurator/ChecklistFieldEntry';
import type { ConfiguratorValue } from '@/interfaces/components/configurator/ConfiguratorValue';
import type { ProductForm } from '@/interfaces/components/configurator/ProductForm';
import type { RadioButtonGroupOptions } from '@/interfaces/components/RadioButtonGroupOptions';
import type { UpdateFunctionEntry } from '@/interfaces/components/configurator/UpdateFunctionEntry';
import type { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import type { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import type { FormValidationPayload } from '@/interfaces/general/FormValidationPayload.js';
import type { MultipositionDeleteMap } from '@/interfaces/general/MultipositionDeleteMap';
import type { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import i18n from '@/i18n';
import PopupNotifications from '@/modules/Notifications/components/PopupNotifications.vue';
import type { PopupNotificationOptions } from '@/modules/Notifications/interfaces/PopupNotificationOptions';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import router from '@/router';
import ConfiguratorService from '@/services/ConfiguratorService';
import store from '@/store';

/* === Props === */

export default /*#__PURE__*/_defineComponent({
  __name: 'AddProductSlot',
  props: {
    popupData: { type: Object as PropType<any>, required: true },
},
  setup(__props) {

const props = __props;



const { proxy } = getCurrentInstance()!;
const componentProxy = proxy as any;

/* === Store Access === */
const getSingleProductForm = (productFormId: string) =>
    store.dispatch('configurator/getSingleProductForm', productFormId);
const productForm = (productFormId: string): ProductForm | null =>
    store.getters['configurator/productForm'](productFormId);
const addActiveProductForm = (
    activeProductFormValue: ActiveProductFormValueObject,
): Promise<number | null> => store.dispatch('configurator/addActiveProductForm', activeProductFormValue);
const updateActiveProductFormValue = (entry: UpdateProductFormEntry) =>
    store.dispatch('configurator/updateActiveProductFormValue', entry);
const resetActiveProductForms = () => store.commit('configurator/resetActiveProductForms');
const getProductFormValues = (productFormId: number): ActiveProductFormValueObject | null =>
    store.getters['configurator/activeProductForm'](productFormId);
const productFormValue = (pId: string, productFormId: number): ConfiguratorValue | null =>
    store.getters['configurator/activeProductFormValue'](pId, productFormId);

/* === State === */
const loadingOverlay = new LoadingOverlayHelper(componentProxy, {});
const activeProductForm = ref<ProductForm | null>(null);
const activeItemRows = ref<ProductRowItem[]>([]);
const initialProductFormValues = ref<ActiveProductFormValueObject | null>(null);
const defaultUpdateFunctions = ref<UpdateFunctionEntry[] | null>(null);
const defaultEditableFields = ref<ChecklistField[]>([]);
const isAddingNewRow = ref(false);
const availableSegments = ref<RadioButtonGroupOptions[]>([]);
const notifications = ref<PopupNotificationOptions[][]>([]);
const flatNotifications = ref<PopupNotificationOptions[]>([]);
const formFieldErrorsExist = ref(false);
let stopCustomFieldsWatcher: (() => void) | null = null;

const debounceSetIsAddingRowToFalse = debounce(setIsAddingRowToFalse, 100);

/* === Derived State === */
const isUpdatingData = computed(
    () => activeItemRows.value.some((activeItemRow) => activeItemRow.isUpdatingData) || isAddingNewRow.value,
);

const isDimensionsWizard = computed(
    () => props.popupData.alreadyInStore && props.popupData.alreadyInStore !== null,
);

const userGroup = computed(() => {
    const currentUserGroupId = store.getters['jwtData/currentUserGroup'];
    const currentUserGroup = UserGroupRepository.getById(String(currentUserGroupId));

    return currentUserGroup || currentUserGroupId;
});

const selectedOmaricaSizes = computed(() =>
    activeItemRows.value.map((itemRow) => productFormValue('p111592', itemRow.activeProductFormId)),
);

/* === Notifications === */
function addNotification(title: string, description: string, errorType: 'error' | 'warning', index = 0) {
    if (notifications.value.length === 0) {
        notifications.value.push([]);
    }

    notifications.value[index].push({
        title: i18n.t(title) as string,
        description: i18n.t(description) as string,
        errorType,
        index: index + 1,
    });
    flatNotifications.value = notifications.value.flat();
}

function onShowNotification(event: any) {
    addNotification(event.title, event.description, event.type, event.index);
}

function onClearNotifications(index: number) {
    notifications.value[index] = [];
    flatNotifications.value = notifications.value.flat();
}

/* === Product Form Setup === */
async function getCurrentProductForm(): Promise<ProductForm | null> {
    const productFormId = props.popupData.productFormId;

    try {
        await getSingleProductForm(productFormId);
    } catch (error) {
        loadingOverlay.stop();
        addNotification(i18n.t('Error occured') as string, (error as Error).message, 'error');
        router.push({ name: RouteNames.error });
        return null;
    }

    return productForm(productFormId);
}

async function setDefaultProductFormSettings() {
    if (activeProductForm.value == null) {
        return Promise.reject(new Error('Greška pri dohvaćanju proizvoda'));
    }

    initialProductFormValues.value = await extractDefaultValues({
        productForm: activeProductForm.value,
        productId: props.popupData.productId,
        clientId: props.popupData.clientId,
        productSystemName: props.popupData.productSystemName,
    });
    defaultUpdateFunctions.value = extractUpdateFunctions(activeProductForm.value);
    defaultEditableFields.value = ConfiguratorService.extractEditableFields(activeProductForm.value);
    availableSegments.value = ConfiguratorService.getSegmentNames(activeProductForm.value);

    return Promise.resolve();
}

/* === Rows === */
async function addNewRow(numberOfRows: number, existingProductValues?: ChecklistFieldEntry[]) {
    isAddingNewRow.value = true;
    notifications.value.push([]);
    let newItemRows: ProductRowItem[];

    try {
        newItemRows = await ConfiguratorService.addNewRow({
            initialProductFormValue: initialProductFormValues.value,
            updateFunctions: defaultUpdateFunctions.value,
            editableFields: defaultEditableFields.value,
            availableSegments: availableSegments.value,
            addActiveProductForm,
            numberOfRows,
            productFormId: props.popupData.productFormId,
            existingProductValues,
            isCustom: activeProductForm.value ? activeProductForm.value.isCustom : false,
            shouldSetDefaultPricelistValue: true,
        });
    } catch (error) {
        addNotification(i18n.t('Error occured') as string, (error as Error).message, 'error');
        isAddingNewRow.value = false;
        return Promise.reject(error);
    }

    setTimeout(() => {
        activeItemRows.value.push(...newItemRows);

        if (typeof numberOfRows !== 'number' || numberOfRows === 1) {
            verifyAndFixNewRowPositioning(newItemRows[0].activeProductFormId);
        }
        debounceSetIsAddingRowToFalse();
    }, 0);

    return Promise.resolve();
}

function addSingleRow() {
    return addNewRow(1);
}

async function verifyAndFixNewRowPositioning(activeProductFormId: number) {
    await nextTick();
    const rowElement = document.getElementById(String(activeProductFormId));

    cssMultipositionRowElementPositioning(rowElement);
}

function setIsAddingRowToFalse() {
    isAddingNewRow.value = false;
}

function updateItemRowsIsUpdatingState(activeProductFormId: number, newState: boolean) {
    const itemRowIndex = findRowIndexByProductFormIdInActiveItemRows(activeProductFormId);
    activeItemRows.value[itemRowIndex].isUpdatingData = newState;
}

function findRowIndexByProductFormIdInActiveItemRows(activeProductFormId: number) {
    return activeItemRows.value.findIndex(
        (itemRow: ProductRowItem) => itemRow.activeProductFormId === activeProductFormId,
    );
}

function deleteExistingRow({ activeProductFormId }: MultipositionDeleteMap) {
    if (activeItemRows.value.length === 1) {
        addNotification(
            i18n.t('Error occured') as string,
            i18n.t('Not possible to delete last row') as string,
            'error',
        );
        return;
    }

    const rowIndexToBeDeleted = activeItemRows.value.findIndex(
        (itemRow: ProductRowItem) => itemRow.activeProductFormId === activeProductFormId,
    );

    if (rowIndexToBeDeleted === -1) {
        return;
    }

    activeItemRows.value.splice(rowIndexToBeDeleted, 1);
    notifications.value.splice(rowIndexToBeDeleted, 1);
    flatNotifications.value = notifications.value.flat();
}

function getProductSlotRowReferences() {
    const segmentedMultipositionReference = componentProxy.$refs.segmentedMultiposition as any;

    if (segmentedMultipositionReference?.$refs?.productSlotRow) {
        return segmentedMultipositionReference.$refs.productSlotRow;
    }

    return [];
}

/* === Validation === */
async function validateFormFields() {
    formFieldErrorsExist.value = false;
    const formValues: FormValidationPayload[] = [];

    for (const itemRow of activeItemRows.value) {
        const rowValues = getProductFormValues(itemRow.activeProductFormId);

        formValues.push({
            rowKey: itemRow.activeProductFormId,
            values: { ...rowValues },
            fieldSegments: itemRow.editableFields as any,
        });
    }

    const errorState = checkForErrorsInFieldsAndReturnState(formValues);
    formFieldErrorsExist.value = errorState.hasErrors;

    if (errorState.hasErrors) {
        showErrorState(errorState.errorDescription);
    }

    EventBus.$emit(EventBusEvents.formValidationFinished);
}

function showErrorState(description: string) {
    notification.error({
        message: i18n.t('Invalid characters detected in form fields:') as string,
        description,
    });
}

/* === Draft Restoration === */
async function handleRestoreDraft(draft: any[]) {
    loadingOverlay.start();
    activeItemRows.value = [];
    notifications.value = [];

    const restoredRows: ProductRowItem[] = [];

    try {
        for (const item of draft) {
            const formValueObject: ActiveProductFormValueObject = {};

            if (Array.isArray(item.formData)) {
                item.formData.forEach((field: any) => {
                    formValueObject[field.id] = field.value;
                });
            }

            const newActiveId = await addActiveProductForm(formValueObject);

            if (newActiveId !== null && newActiveId !== undefined) {
                restoredRows.push({
                    activeProductFormId: newActiveId,
                    productFormId: item.activeProductFormId || props.popupData.productFormId,
                    production: item.production,
                    connection: item.connection,
                    editableFields: defaultEditableFields.value,
                    availableSegments: availableSegments.value,
                    updateFunctions: defaultUpdateFunctions.value,
                    isUpdatingData: false,
                    productName: props.popupData.productName,
                    productId: props.popupData.productId,
                } as ProductRowItem);

                notifications.value.push([]);
            }
        }

        activeItemRows.value = restoredRows;
        flatNotifications.value = notifications.value.flat();

        notification.success({
            message: i18n.t('Draft restored') as string,
            description: i18n.t('Successfully restored draft items.') as string,
        });
    } catch (error) {
        notification.error({
            message: i18n.t('Restore failed') as string,
            description: (error as Error).message,
        });
    } finally {
        loadingOverlay.stop();
    }
}

/* === Dimensions Wizard === */
function setCustomFieldValues() {
    const rowData = props.popupData.customDimensions;

    activeItemRows.value.forEach(async (itemRow: ProductRowItem, index: number) => {
        const fields = isDimensionsWizard.value ? rowData : rowData[index].fields;

        fields.forEach(async (field: any) => {
            let timeout = 0;

            if (field.pId === 'p740593') {
                timeout = 100;
            }

            setTimeout(() => {
                updateActiveProductFormValue({
                    pId: field.pId,
                    value: field.value,
                    productFormId: itemRow.activeProductFormId,
                });
            }, timeout);
        });
    });
}

function checkIfThereAreErrorsInHeightCalculation(selectedValues: any[]) {
    return selectedValues.some((value: any) => value === 'ERR' || value === '#' || value === '');
}

function setupCustomFieldsWatcher() {
    stopCustomFieldsWatcher = watch(selectedOmaricaSizes, handleCustomFieldsChange);
}

function unwatchCustomFields() {
    if (stopCustomFieldsWatcher) {
        stopCustomFieldsWatcher();
        stopCustomFieldsWatcher = null;
    }
}

function handleCustomFieldsChange(newValue: any[]) {
    if (checkIfThereAreErrorsInHeightCalculation(newValue)) {
        return;
    }

    nextTick(() => {
        setCustomFieldValues();
        unwatchCustomFields();
    });
}

function nukeDefaultTooltips() {
    const wrapperElement = document.querySelector('.co-add-product-popup-slot') as HTMLElement;
    const paginationItems = wrapperElement.querySelectorAll(
        '.ant-pagination-item, .ant-pagination-prev, .ant-pagination-next',
    );

    paginationItems.forEach((item) => {
        item.removeAttribute('title');
    });
}

function onCancel() {
    EventBus.$emit(EventBusEvents.closePopup, true);
}

/* === Initialization & Cleanup === */
(async () => {
    resetHelperState();
    loadingOverlay.start();
    activeProductForm.value = await getCurrentProductForm();

    try {
        await setDefaultProductFormSettings();
    } catch (error) {
        addNotification(i18n.t('Error occured') as string, (error as Error).message, 'error');
        loadingOverlay.stop();
        return;
    }

    try {
        if (isDimensionsWizard.value) {
            const currentProductFormValues = getProductFormValues(parseInt(props.popupData.alreadyInStore, 10));

            if (currentProductFormValues) {
                const existingProductValues = Object.keys(currentProductFormValues).map((key: string) => ({
                    id: key,
                    value: currentProductFormValues[key],
                })) as ChecklistFieldEntry[];

                await addNewRow(props.popupData.timesToMultiply, existingProductValues);
            }
        } else if (props.popupData.isRestoringDraft && props.popupData.restoredRowData) {
            await handleRestoreDraft(props.popupData.restoredRowData);
        } else {
            await addNewRow(props.popupData.timesToMultiply);
        }
    } catch (error) {
        addNotification(i18n.t('Error occured') as string, (error as Error).message, 'error');
        loadingOverlay.stop();
        return;
    }

    nukeDefaultTooltips();
    loadingOverlay.stop();

    EventBus.$on(EventBusEvents.showNotification, onShowNotification);
    EventBus.$on(EventBusEvents.clearNotifications, onClearNotifications);
})();

onMounted(() => {
    if (props.popupData.customDimensions) {
        setupCustomFieldsWatcher();
    }
});

onBeforeUnmount(() => {
    notifications.value = [];
    unwatchCustomFields();
    resetActiveProductForms();
    notification.destroy();
    EventBus.$off(EventBusEvents.showNotification, onShowNotification);
    EventBus.$off(EventBusEvents.clearNotifications, onClearNotifications);
});

return { __sfc: true,props, proxy, componentProxy, getSingleProductForm, productForm, addActiveProductForm, updateActiveProductFormValue, resetActiveProductForms, getProductFormValues, productFormValue, loadingOverlay, activeProductForm, activeItemRows, initialProductFormValues, defaultUpdateFunctions, defaultEditableFields, isAddingNewRow, availableSegments, notifications, flatNotifications, formFieldErrorsExist, stopCustomFieldsWatcher, debounceSetIsAddingRowToFalse, isUpdatingData, isDimensionsWizard, userGroup, selectedOmaricaSizes, addNotification, onShowNotification, onClearNotifications, getCurrentProductForm, setDefaultProductFormSettings, addNewRow, addSingleRow, verifyAndFixNewRowPositioning, setIsAddingRowToFalse, updateItemRowsIsUpdatingState, findRowIndexByProductFormIdInActiveItemRows, deleteExistingRow, getProductSlotRowReferences, validateFormFields, showErrorState, handleRestoreDraft, setCustomFieldValues, checkIfThereAreErrorsInHeightCalculation, setupCustomFieldsWatcher, unwatchCustomFields, handleCustomFieldsChange, nukeDefaultTooltips, onCancel, AddOfferItems, SegmentedMultiposition, PopupNotifications }
}

})